

















































































































import {
	computed,
	defineComponent,
	onMounted,
	PropType,
	reactive,
	ref,
	Ref,
	toRef,
	toRefs,
	watch,
} from '@vue/composition-api';
import deepEqual from 'deep-equal';
import { trackGroupSimpleAction } from '@front-app/services/ActionApi/ActionApi';
import { GroupAuditAction } from '@front-app/services/ActionApi/ActionModel';
import { STATUS } from '@front-app/constants/state';
import { Size } from '@front-app/components/constants';
import { ButtonPalettes } from '@front-app/components/buttons/palettes';
import { useTiptapEditor, EMPTY_MESSAGE } from '@front-app/components/form/tiptap/useTiptapEditor';
import { UserSearchItem } from '@front-app/services/UserApi/UserModel';
import { CreateNotePayload } from '@front-app/services/NoteApi/NoteApi';
import { UserStore } from '@front-app/store/singletons/User';
import { v } from '@front-app/components/form/validation/useValidation';
import { required } from '@front-app/components/form/validation/validators';
import { Content } from '@front-app/services/GroupsApi/DiscussionModel';
import TrForm from '@front-app/components/form/Form.vue';
import FormSection from '@front-app/components/form/FormSection.vue';
import FormLabel from '@front-app/components/form/FormLabel.vue';
import FormError from '@front-app/components/form/FormError.vue';
import FormInput from '@front-app/components/form/FormInput.vue';
import EditorMenu from '@front-app/components/form/tiptap/EditorMenu.vue';
import EditorContent from '@front-app/components/form/tiptap/EditorContent.vue';
import EditorContainer from '@front-app/components/form/tiptap/EditorContainer.vue';
import CtaButton from '@front-app/components/buttons/CtaButton.vue';
import { useGroupMembersStore } from '@front-app/store/singletons/GroupMembers';
import { SearchOption } from '../search/useSearch';
import SearchGroupUsers from '../inputs/SearchGroupUsers.vue';
import SelectedUserButton from '../buttons/SelectedUserButton.vue';
import NoteContextHeader from './NoteContextHeader.vue';

export default defineComponent({
	components: {
		TrForm,
		FormSection,
		FormLabel,
		FormError,
		FormInput,
		EditorMenu,
		EditorContent,
		EditorContainer,
		CtaButton,
		SearchGroupUsers,
		SelectedUserButton,
		NoteContextHeader,
	},
	props: {
		discussionMessageId: {
			type: String,
			required: true,
		},
		groupId: {
			type: String,
			required: true,
		},
		discussion: {
			type: Object as PropType<Content>,
			required: true,
		},
		isCreatingNote: Boolean,
		isSuggestCategories: Boolean,
	},
	setup(props, ctx) {
		const state = reactive({
			message: EMPTY_MESSAGE,
			subjectText: '',
		});

		const trackActionProps = computed(() => [props.groupId, props.discussionMessageId]);
		watch(trackActionProps, (curr, prev) => {
			if (deepEqual(curr, prev) || !props.groupId || !props.discussionMessageId) {
				return;
			}
			trackGroupSimpleAction(props.groupId, GroupAuditAction.VIEW_NOTE_CREATE_PAGE);
		}, { immediate: true });

		// Validation
		const validation = v({
			subject: {
				value: toRef(state, 'subjectText'),
				rules: [required()],
			},
			message: {
				value: toRef(state, 'message'),
				rules: [required()],
			},
		});

		// Participant invites
		const selectedUserIds: Ref<string[]> = ref([]);
		const selectUser = (user: SearchOption<UserSearchItem>) => {
			UserStore.getInstance().setPartial({
				...user.original,
				status: STATUS.SUCCESS,
			});
			if (!selectedUserIds.value.includes(user.original.id)) {
				selectedUserIds.value = [user.original.id, ...selectedUserIds.value];
			}
		};
		const removeUser = (userId: string) => {
			selectedUserIds.value = selectedUserIds.value.filter((id: string) => id !== userId);
		};

		// Message
		const el = ref(null as null | Element);
		const { editor } = useTiptapEditor(el);
		const updateMessage = () => {
			state.message = editor.getHTML();
			if (state.message === EMPTY_MESSAGE) {
				state.message = '';
			}
		};
		editor.on('blur', async () => {
			updateMessage();
			validation.fields.message.validate();
		});

		// Suggest categories
		const {	getAdministrators	} = useGroupMembersStore(computed(() => props.groupId));
		onMounted(() => {
			if (props.isSuggestCategories) {
				const administrators = getAdministrators();
				selectedUserIds.value = administrators.map((user) => user.id);
				state.subjectText = ctx.root.$t('create-note.suggest-categories.subject') as unknown as string;
				editor.commands.setContent(`<p>${ctx.root.$t('create-note.suggest-categories.message')}</p>`);
			}
		});

		const attemptSubmit = async () => {
			updateMessage();
			validation.validate();
			if (validation.isValid.value) {
				const payload: CreateNotePayload = {
					discussionMessageId: props.discussionMessageId,
					to: selectedUserIds.value,
					subject: state.subjectText,
					message: state.message,
				};
				ctx.emit('submit', payload);
			} else if (!validation.fields.subject.isValid) {
				(document.querySelector('#subject_text') as null | HTMLInputElement)?.focus();
			} else {
				editor.chain().focus().run();
			}
		};

		return {
			...toRefs(state),
			validation,
			// Message text editor
			el,
			editor,
			attemptSubmit,
			// Participant invites
			selectedUserIds,
			selectUser,
			removeUser,
			// Util
			Size,
			ButtonPalettes,
		};
	},
});
